import { z } from "zod";
import {
  AgentId,
  ApprovalId,
  ConversationId,
  CredentialId,
  Locale,
  LocalizedText,
  MessageId,
  NodeId,
  ProjectId,
  SessionId,
  TaskId,
  TemplateId,
  TenantId,
  Timestamp,
  UserId,
} from "./common.js";

// ───────────── 账户 / 租户 ─────────────

export const Theme = z.enum(["system", "light", "dark"]);

export const User = z.object({
  id: UserId,
  email: z.email(),
  displayName: z.string().min(1).max(64),
  locale: Locale,
  timezone: z.string().default("UTC"),
  theme: Theme.default("system"),
  createdAt: Timestamp,
});
export type User = z.infer<typeof User>;

export const TenantRole = z.enum(["owner", "admin", "member", "viewer"]);
export type TenantRole = z.infer<typeof TenantRole>;

export const Tenant = z.object({
  id: TenantId,
  name: z.string().min(1).max(64),
  slug: z.string().regex(/^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/),
  createdAt: Timestamp,
});
export type Tenant = z.infer<typeof Tenant>;

export const Membership = z.object({
  tenantId: TenantId,
  userId: UserId,
  role: TenantRole,
  user: User.pick({ id: true, email: true, displayName: true }).optional(),
});
export type Membership = z.infer<typeof Membership>;

// ───────────── 凭据（secret 永不回传） ─────────────

/** pool：凭据池，由多个 subscription 凭据组成，llm-gw 按请求自动选择与切换 */
export const CredentialKind = z.enum(["subscription", "platform", "pool"]);
export type CredentialKind = z.infer<typeof CredentialKind>;

export const Credential = z.object({
  id: CredentialId,
  tenantId: TenantId,
  kind: CredentialKind,
  label: z.string().max(64),
  /** subscription 凭据只能绑定该成员本人所在项目的 agent（architecture §4 合规边界） */
  ownerUserId: UserId.nullable(),
  status: z.enum(["unverified", "valid", "invalid", "revoked"]),
  /** 形如 "sk-…abcd"，只用于辨认 */
  hint: z.string().nullable(),
  createdAt: Timestamp,
  /** 仅 pool：成员凭据（按优先顺序） */
  memberIds: z.array(CredentialId).optional(),
  /** 仅 subscription：运行时健康状况（限流冷却、上游报告的额度利用率、最近 5 小时用量） */
  runtime: z
    .object({
      /** 被限流时预计恢复的时间；null 表示可用 */
      coolingUntil: Timestamp.nullable(),
      /** 上游响应头报告的额度利用率 0–1（取各窗口的最大值）；未知为 null */
      utilization: z.number().nullable(),
      /** 最近 5 小时经 llm-gw 的 token 用量（输入 + 输出 + 缓存） */
      tokens5h: z.number().int().nonnegative(),
    })
    .optional(),
});
export type Credential = z.infer<typeof Credential>;

// ───────────── 项目 ─────────────

export const Project = z.object({
  id: ProjectId,
  tenantId: TenantId,
  name: z.string().min(1).max(64),
  description: z.string().max(2000).default(""),
  /** agent 产出使用的语言（写入 CLAUDE.md），与 UI locale 无关 */
  locale: Locale,
  createdAt: Timestamp,
});
export type Project = z.infer<typeof Project>;

// ───────────── 身份模板 ─────────────

export const PermissionMode = z.enum(["ask", "allow", "deny"]);

export const PermissionPolicy = z.object({
  /** 默认对未命中规则的工具调用的处理 */
  default: PermissionMode.default("ask"),
  /** 规则按顺序匹配，形如 { tool: "Bash", pattern: "npm test*", mode: "allow" } */
  rules: z
    .array(z.object({ tool: z.string(), pattern: z.string().optional(), mode: PermissionMode }))
    .default([]),
});
export type PermissionPolicy = z.infer<typeof PermissionPolicy>;

export const McpScope = z.enum(["identity", "mail", "chat", "meeting", "task", "code", "asset", "human", "orchestrate"]);
export type McpScope = z.infer<typeof McpScope>;

/** 能力包引用（详见 capability.ts）；放在这里是为了 Template / Agent 引用时避免循环依赖 */
export const CapabilityId = z.string().regex(/^[a-z][a-z0-9-]{1,47}$/, "capability id: 小写字母 / 数字 / -");
export type CapabilityId = z.infer<typeof CapabilityId>;
export const CapabilityRef = z.object({
  id: CapabilityId,
  /** 固定版本；null = 跟随最新 */
  version: z.number().int().positive().nullable().default(null),
  params: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).default({}),
});
export type CapabilityRef = z.infer<typeof CapabilityRef>;
export const AgentCapabilityOverrides = z.object({
  add: z.array(CapabilityRef).default([]),
  remove: z.array(CapabilityId).default([]),
});
export type AgentCapabilityOverrides = z.infer<typeof AgentCapabilityOverrides>;

export const Template = z.object({
  id: TemplateId,
  tenantId: TenantId.nullable(), // null = 平台内置
  role: z.string().regex(/^[a-z][a-z0-9-]{1,31}$/), // frontend / backend / art / planner / orchestrator …
  name: LocalizedText,
  description: LocalizedText,
  version: z.number().int().positive(),
  systemPrompt: z.string(), // 渲染为 CLAUDE.md 的角色段
  skills: z.array(z.object({ name: z.string(), content: z.string() })).default([]),
  allowedTools: z.array(z.string()).default([]),
  mcpScopes: z.array(McpScope).default(["identity"]),
  image: z.string().default("agentsaas/agent-base:latest"),
  defaultModel: z.string(),
  permissionPolicy: PermissionPolicy,
  resources: z.object({ cpus: z.number().positive(), memoryMb: z.number().int().positive() }).default({ cpus: 2, memoryMb: 4096 }),
  /** 引用的能力包（skill / 工具 / 运行环境）；人设之外的能力都来自这里 */
  capabilities: z.array(CapabilityRef).optional(),
});
export type Template = z.infer<typeof Template>;

// ───────────── Agent ─────────────

export const AgentStatus = z.enum([
  "provisioning",
  "idle",
  "busy",
  "waiting_approval",
  "paused",
  "budget_exhausted",
  "unreachable",
  "error",
]);
export type AgentStatus = z.infer<typeof AgentStatus>;

export const Budget = z.object({
  /** 月度上限，单位 micro-USD；null = 不限（仍受租户级限制） */
  monthlyLimitMicroUsd: z.number().int().nonnegative().nullable(),
  usedMicroUsd: z.number().int().nonnegative().default(0),
});

export const Agent = z.object({
  id: AgentId,
  projectId: ProjectId,
  templateId: TemplateId,
  templateVersion: z.number().int().positive(),
  name: z.string().regex(/^[\p{L}\p{N}][\p{L}\p{N}_-]{0,31}$/u),
  role: z.string(),
  credentialId: CredentialId,
  status: AgentStatus,
  budget: Budget,
  currentSessionId: SessionId.nullable(),
  /** 当前在做的任务（assignee = 该 agent 且状态为 working / input_required / auth_required）；P2+，旧数据缺省 null */
  currentTaskId: TaskId.nullable().optional(),
  /** 实例级能力增减（在模板的能力包之上） */
  capabilityOverrides: AgentCapabilityOverrides.optional(),
  createdAt: Timestamp,
});
export type Agent = z.infer<typeof Agent>;

/** 仅管理员视图可见 */
export const AgentPlacement = z.object({
  agentId: AgentId,
  nodeId: NodeId.nullable(),
  pinned: z.boolean(),
  /** 管理台展示用（控制面总是返回；agent 已删除时缺省） */
  agentName: z.string().optional(),
  projectId: ProjectId.optional(),
});

// ───────────── 审批 ─────────────

export const ApprovalDecision = z.enum(["allow", "deny"]);
export const Approval = z.object({
  id: ApprovalId,
  projectId: ProjectId,
  agentId: AgentId,
  tool: z.string(),
  input: z.unknown(),
  reason: z.string().nullable(),
  status: z.enum(["pending", "allowed", "denied", "expired"]),
  decidedBy: UserId.nullable(),
  createdAt: Timestamp,
  resolvedAt: Timestamp.nullable(),
});
export type Approval = z.infer<typeof Approval>;

// ───────────── 文件 ─────────────

/** 容器内相对 work 根的路径；禁止 .. 与绝对路径（服务端仍会 realpath 校验）。 */
export const FsPath = z
  .string()
  .max(1024)
  .refine((p) => !p.startsWith("/") && !p.split("/").includes(".."), "fs.path_invalid");

export const FsEntry = z.object({
  path: FsPath,
  name: z.string(),
  type: z.enum(["file", "dir", "symlink"]),
  size: z.number().int().nonnegative(),
  mtime: Timestamp,
});
export type FsEntry = z.infer<typeof FsEntry>;

export const FsChangeKind = z.enum(["created", "modified", "deleted"]);

// ───────────── 会话 / 消息 ─────────────

export const Actor = z.discriminatedUnion("type", [
  z.object({ type: z.literal("user"), id: UserId }),
  z.object({ type: z.literal("agent"), id: AgentId }),
  z.object({ type: z.literal("system") }),
]);
export type Actor = z.infer<typeof Actor>;

/** 附件引用：git://repo@commit:path | asset://id@ver | shared://path | work://path */
export const Ref = z.string().regex(/^(git|asset|shared|work):\/\/.+/);

export const Part = z.discriminatedUnion("type", [
  z.object({ type: z.literal("text"), text: z.string() }),
  z.object({ type: z.literal("ref"), ref: Ref, name: z.string().optional(), mediaType: z.string().optional() }),
]);
export type Part = z.infer<typeof Part>;

export const ConversationKind = z.enum(["dm", "room", "meeting", "mail_thread"]);
/** 会话被护栏暂停的原因 */
export const GuardReason = z.enum(["hop_limit", "budget", "loop_detected", "manual"]);
export type GuardReason = z.infer<typeof GuardReason>;

export const Conversation = z.object({
  id: ConversationId,
  projectId: ProjectId,
  kind: ConversationKind,
  title: z.string().nullable(),
  members: z.array(Actor),
  hopLimit: z.number().int().positive().default(6),
  paused: z.boolean().default(false),
  /** 暂停原因（paused=false 时为 null）；P3+ */
  pausedReason: GuardReason.nullable().optional(),
  /** 房间 / 会议的 token 预算（micro-USD）；null = 不限（仍受 agent / 租户预算约束） */
  budgetMicroUsd: z.number().int().nonnegative().nullable().default(null),
  usedMicroUsd: z.number().int().nonnegative().default(0),
  muted: z.boolean().default(false),
  createdAt: Timestamp,
});
export type Conversation = z.infer<typeof Conversation>;

export const Message = z.object({
  id: MessageId,
  conversationId: ConversationId,
  from: Actor,
  parts: z.array(Part).min(1),
  mentions: z.array(AgentId).default([]),
  replyTo: MessageId.nullable(),
  /** 由 bus 盖章，发送方不可自报 */
  hop: z.number().int().nonnegative(),
  createdAt: Timestamp,
});
export type Message = z.infer<typeof Message>;

export const TaskRef = z.object({ id: TaskId, title: z.string() });
